import FlagCircleIcon from '@mui/icons-material/FlagCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    alpha,
    Badge,
    Box,
    Button,
    Chip,
    CircularProgress,
    Fade,
    Paper,
    Tooltip,
    Typography,
    styled,
    tooltipClasses,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import isColorDark from '../../utils/isColorDark';
import PublicCardItem from './PublicCardItem';

const SCROLL_THRESHOLD = 1200;

const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};

const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#1f2937',
        color: '#ffffff',
        maxWidth: 300,
        fontSize: '0.875rem',
        border: `1px solid ${alpha('#ffffff', 0.1)}`,
        borderRadius: '8px',
        padding: '12px 16px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    },
    [`& .${tooltipClasses.arrow}`]: { color: '#1f2937' },
}));

const TooltipContent = ({ title, description, color }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.9rem', fontWeight: 600, color: '#ffffff' }}>
            <FlagCircleIcon sx={{ fontSize: '16px', color }} />
            {title}
        </Box>
        <Typography variant="body2" sx={{ color: '#e5e7eb', fontSize: '0.8125rem', lineHeight: 1.5 }}>
            {description}
        </Typography>
    </Box>
);

const RenderPropsIcon = ({ hasStart, hasFinished, hasConcluded }) => {
    if (!hasStart && !hasFinished && !hasConcluded) return null;
    const color = hasConcluded ? '#00C853' : hasFinished ? '#FFD600' : '#2962FF';
    const tooltipConfig = hasConcluded
        ? { title: 'Tarefa Concluída', description: 'Cards nesta coluna estão concluídos.' }
        : hasFinished
        ? { title: 'Tarefa Finalizada', description: 'Cards nesta coluna estão em revisão.' }
        : { title: 'Tarefa Iniciada', description: 'Cards nesta coluna estão em andamento.' };
    return (
        <CustomTooltip
            title={<TooltipContent title={tooltipConfig.title} description={tooltipConfig.description} color={color} />}
            placement="left"
            arrow
        >
            <Box sx={{ display: 'flex', cursor: 'help' }}>
                <FlagCircleIcon sx={{ color, fontSize: '25px' }} />
            </Box>
        </CustomTooltip>
    );
};

const PublicColumn = React.memo(function PublicColumn({ column, displayFilters, onOpenCard, onLoadMore }) {
    const cards = column.cards || [];
    const containerRef = useRef(null);
    const loadingRef = useRef(false);
    const [isLoading, setIsLoading] = useState(false);

    const hasMore = !!column?.pagination?.hasMore;
    const totalCount = column?.pagination?.count ?? cards.length;

    const loadMoreItems = useCallback(() => {
        if (loadingRef.current || !hasMore) return;
        loadingRef.current = true;
        setIsLoading(true);
        const p = onLoadMore?.(column.id);
        if (p && typeof p.then === 'function') {
            p.catch((err) => console.error('Erro ao carregar mais itens:', err)).finally(() => {
                loadingRef.current = false;
                setIsLoading(false);
            });
        } else {
            setTimeout(() => {
                loadingRef.current = false;
                setIsLoading(false);
            }, 500);
        }
    }, [column.id, hasMore, onLoadMore]);

    const debouncedLoadMore = useCallback(debounce(loadMoreItems, 150), [loadMoreItems]);

    const handleScroll = useCallback(
        (e) => {
            const target = e.target;
            const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
            if (scrollBottom < SCROLL_THRESHOLD) debouncedLoadMore();
        },
        [debouncedLoadMore]
    );

    useEffect(() => () => { loadingRef.current = false; }, []);

    return (
        <Badge
            sx={{
                '& .MuiBadge-badge': {
                    top: '10px',
                    left: '20px',
                    padding: '0px',
                    border: theme => `2px solid ${alpha(column?.color || theme.palette.primary.main, 0.9)}`,
                    backgroundColor: theme => theme.palette.mode === 'dark' ? '#161c24' : '#f9f9f9',
                    height: '28px',
                    borderRadius: '35px',
                },
            }}
            badgeContent={
                !column?.hasStart && !column?.hasFinished && !column?.hasConcluded ? null : (
                    <RenderPropsIcon
                        hasStart={!!column?.hasStart}
                        hasFinished={!!column?.hasFinished}
                        hasConcluded={!!column?.hasConcluded}
                    />
                )
            }
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
            <Paper
                elevation={1}
                sx={{
                    width: 300,
                    margin: 0.75,
                    backgroundColor: theme => alpha(column?.color || theme.palette.primary.main, 0.2),
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(100vh - 175px)',
                    border: theme => `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.1) : theme.palette.divider}`,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        boxShadow: theme => theme.palette.mode === 'dark'
                            ? '0 8px 16px rgba(0,0,0,0.3)'
                            : '0 6px 12px rgba(0,0,0,0.1)',
                    },
                }}
            >
                <Box
                    sx={{
                        py: 1.25,
                        px: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: theme => column?.color || theme.palette.primary.main,
                        borderBottom: theme => `1px solid ${alpha(theme.palette.primary.dark, 0.2)}`,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {displayFilters?.showTaskCount !== false && (
                            <Chip
                                variant="contained"
                                label={totalCount}
                                size="small"
                                sx={{
                                    marginRight: '10px',
                                    height: '19px',
                                    backgroundColor: (theme) => column?.color
                                        ? isColorDark(column.color) ? '#ffffff' : '#000000'
                                        : theme.palette.mode === 'light' ? '#000000' : '#ffffff',
                                    color: (theme) => column?.color
                                        ? isColorDark(column.color) ? '#000000' : '#ffffff'
                                        : theme.palette.mode === 'light' ? '#ffffff' : '#000000',
                                }}
                            />
                        )}
                        <Typography
                            variant="body1"
                            fontWeight="bold"
                            whiteSpace="pre-wrap"
                            sx={{
                                color: (theme) => column?.color
                                    ? isColorDark(column.color) ? '#ffffff' : '#000000'
                                    : theme.palette.mode === 'light' ? '#000000' : '#ffffff',
                            }}
                        >
                            {column.title}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    ref={containerRef}
                    onScroll={handleScroll}
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        padding: '15px 0px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        backgroundColor: theme => theme.palette.mode === 'light'
                            ? 'rgba(243, 243, 243, 0.03)'
                            : 'rgba(255,255,255,0.03)',
                    }}
                >
                    {cards.map((card) => (
                        <Box key={card.id} sx={{ px: 0.75 }}>
                            <PublicCardItem item={card} displayFilters={displayFilters} onOpen={onOpenCard} />
                        </Box>
                    ))}

                    {cards.length === 0 && !isLoading && (
                        <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                            <Typography variant="caption">Sem cards</Typography>
                        </Box>
                    )}

                    <Fade in={isLoading} timeout={300}>
                        <Box
                            sx={{
                                display: isLoading ? 'flex' : 'none',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                my: 2,
                                py: 1.5,
                                backgroundColor: theme => theme.palette.mode === 'light'
                                    ? 'rgba(0, 0, 0, 0.04)'
                                    : 'rgba(255, 255, 255, 0.05)',
                                borderRadius: 1.5,
                                mx: 1.5,
                            }}
                        >
                            <CircularProgress size={24} thickness={4} sx={{ color: theme => theme.palette.primary.main, mb: 0.5 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                                Carregando...
                            </Typography>
                        </Box>
                    </Fade>

                    {hasMore && !isLoading && (
                        <Fade in={!isLoading} timeout={300}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    py: 1.5,
                                    mt: 1,
                                    mx: 1.5,
                                    borderTop: '1px dashed rgba(255,255,255,0.1)',
                                    borderRadius: 1,
                                    backgroundColor: theme => theme.palette.mode === 'light'
                                        ? 'rgba(0, 0, 0, 0.02)'
                                        : 'rgba(255, 255, 255, 0.02)',
                                    '&:hover': {
                                        backgroundColor: theme => theme.palette.mode === 'light'
                                            ? 'rgba(0, 0, 0, 0.04)'
                                            : 'rgba(255, 255, 255, 0.04)',
                                    },
                                }}
                            >
                                <Button
                                    variant="text"
                                    size="small"
                                    endIcon={<ExpandMoreIcon />}
                                    onClick={loadMoreItems}
                                    disabled={loadingRef.current}
                                    sx={{
                                        fontSize: '0.75rem',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        py: 0.5,
                                        px: 2,
                                        borderRadius: 2,
                                    }}
                                >
                                    Carregar mais
                                </Button>
                            </Box>
                        </Fade>
                    )}
                </Box>

                <Box
                    sx={{
                        p: 1.5,
                        mt: 'auto',
                        borderTop: theme => `1px solid ${alpha(column?.color || theme.palette.primary.main, 0.1)}`,
                        backgroundColor: theme => alpha(column?.color || theme.palette.primary.main, 0.6),
                        boxShadow: '0 -1px 3px rgba(0,0,0,0.02)',
                    }}
                />
            </Paper>
        </Badge>
    );
});

export default PublicColumn;
